/**
 * Retrieval (§11, §19). Walking into a conversation with Marco assembles a
 * briefing: his card, facts about your relationship, impressions relevant to
 * the topic, the last few interactions, anything he's heard recently.
 *
 * Deterministic array filters over tags, recency and permanence flags.
 * No embeddings, no vector database (§22). Permanent memories always
 * retrieve; decaying ones retrieve when they match the topic and haven't
 * faded; everything is capped so the result is a briefing, not a history.
 *
 * `assembleBriefing` is the one permitted producer of what the model is told
 * about a person. "Constrain the input, don't validate the output" (§14)
 * only works if there is exactly one place that constructs the input, so:
 *
 *  - What he knows is what he is party to, saw, or was told. Never world
 *    truth about someone else because he happens to be bonded to them.
 *  - What is in the room — who is present, who is being spoken to, what
 *    each of them is carrying — is stated, including when the answer is
 *    "nothing". An empty list is a fact the model is told, not an omission
 *    it can fill.
 *  - An appointment is where it stands against now. No clock leaves here
 *    for one; the sim already enforces the time, the model only needs to
 *    react to it.
 *
 * State is passed as numbers, never prose (§11). This module builds data.
 * Turning it into a prompt is the server's job.
 */

import { conversationMode, type ConversationMode, modeReasons, type ModeReasons } from './conversation.js';
import { entityTag } from '../sim/ids.js';
import { eventsBetween } from './memory/events.js';
import { factsBetween } from './memory/facts.js';
import { impressionsOf } from './memory/impressions.js';
import { textureConcerning } from './memory/texture.js';
import { npcsAt, whereIs } from '../sim/presence.js';
import { contradictions } from '../sim/proposition.js';
import { minutesBetween, MINUTES_PER_DAY } from '../sim/time.js';
import { TUNING } from '../sim/tuning.js';
import type {
  Actor,
  Appointment,
  AppointmentId,
  Contradiction,
  EmotionalState,
  EntityRef,
  Event,
  Fact,
  Flaw,
  Impression,
  Injury,
  LocationId,
  NarrativeTexture,
  NpcId,
  NpcReadLayer,
  NpcTrueLayer,
  Possession,
  Purpose,
  Secret,
  Stats,
  VoiceBlock,
  WorldState,
  WorldTime,
} from '../sim/types/index.js';

/** What the conversation is about. Both parts optional; empty means "nothing in particular". */
export interface Topic {
  readonly tags?: readonly string[];
  readonly about?: readonly EntityRef[];
}

/**
 * Where an appointment sits relative to right now (§15). The sim already
 * enforces the four enforceable fields of a promise; this reads the clock
 * against them so the model can react to a deadline instead of only being
 * handed a bare time (bug: "Gus told me to come at 6. I showed up near 6
 * and he didn't acknowledge it").
 *
 * Boundaries (implementation-level tuning, not a design decision — the spec
 * fixes the shape of a promise, not this curve, §21):
 *  - `due_now` is `TUNING.appointments.dueNowWindowMinutes` either side of
 *    the stated time — "showed up near 6" reads as *now*, whether that's a
 *    few minutes early or a few minutes late.
 *  - `due_soon` is everything still further ahead of that window — however
 *    far off, it hasn't happened yet, and there is no fixed cutoff a model
 *    can't just be told the actual minutes remaining for.
 *  - `late` is past the `due_now` window but still inside
 *    `TUNING.appointments.graceMinutes` (the same window `keepAppointments`
 *    in sim/reducer.ts treats as "still on time" for keeping the promise,
 *    and `lapseAppointments` uses as the no-show deadline): the moment has
 *    passed and it's no longer "now", but it isn't blown yet either.
 *    `dueNowWindowMinutes` is kept smaller than `graceMinutes` specifically
 *    so this state is reachable.
 *  - `missed` mirrors `appt.status.kind === 'missed'` directly. By
 *    construction, `lapseAppointments` flips status the instant the clock
 *    crosses the grace deadline, in the same `passTime` call that gets the
 *    clock there — so a still-`pending` appointment can never actually be
 *    timed past the grace window in normal play. The `delta > grace` branch
 *    below exists for completeness and for callers who pass an appointment
 *    object frozen before that flip landed.
 */
export type AppointmentTiming = 'due_soon' | 'due_now' | 'late' | 'missed';

export function appointmentTiming(now: WorldTime, appt: Appointment): AppointmentTiming {
  if (appt.status.kind === 'missed') return 'missed';
  const grace = TUNING.appointments.graceMinutes;
  const dueNow = TUNING.appointments.dueNowWindowMinutes;
  const delta = minutesBetween(appt.time, now); // now - appt.time: negative before, positive after.
  if (delta > grace) return 'missed';
  if (delta > dueNow) return 'late';
  if (delta >= -dueNow) return 'due_now';
  return 'due_soon';
}

/**
 * An appointment as the model hears about it: the four enforceable fields
 * minus the clock, plus where it stands. `minutesFromNow` is signed and
 * relative — positive is still ahead, negative is already past — so the
 * server can say "due in 20m" or "40m late" without ever seeing a
 * `WorldTime`. Deliberately not an `Appointment`: that type carries `time`
 * and `madeOn`, and neither may reach the model from here.
 */
export interface BriefedAppointment {
  readonly id: AppointmentId;
  readonly place: LocationId;
  readonly participants: readonly Actor[];
  readonly purpose: Purpose;
  readonly promisedBy: Actor;
  readonly timing: AppointmentTiming;
  readonly minutesFromNow: number;
}

export interface Briefing {
  readonly npc: {
    readonly id: NpcId;
    readonly name: string;
    /** One line: what he is on the block. */
    readonly role: string;
    readonly voice: VoiceBlock;
    readonly stats: Stats;
    readonly flaw: Flaw;
    /**
     * What he wants, from his card. This is also the "what is he trying to
     * get out of this scene" field, for now.
     */
    // OPEN: whether a per-scene want (a situational goal distinct from the
    // card's standing wants) should exist and where it would come from. The
    // instruction that asked for it referred to a note that never arrived;
    // nothing in the codebase models one, so the card's wants stand in.
    readonly wants: readonly string[];
    readonly mood: EmotionalState;
    readonly injuries: readonly Injury[];
  };
  /** True numbers. The model is told, never asked to recall (§11). */
  readonly truth: Pick<NpcTrueLayer, 'relationship' | 'stances' | 'loyalties'>;
  /** What the player character believes, with confidence (§12). */
  readonly read: Pick<NpcReadLayer, 'relationship' | 'stances' | 'confidence' | 'acquaintance'>;
  /**
   * Who is in the room and who is being spoken to. The player first, then
   * every NPC at his location (him included), in the stable `npcsAt` order.
   * The others are present and hear everything; the model is told so rather
   * than left to notice from the manifest.
   */
  readonly participants: readonly Actor[];
  readonly addressee: NpcId;
  /**
   * What each of them is carrying, read straight off the state. Empty means
   * empty: the server renders it as an explicit "nothing".
   */
  readonly possessions: {
    readonly npc: readonly Possession[];
    readonly player: readonly Possession[];
  };
  /** The ledger between him and the player, and nothing about anyone else (§12). */
  readonly facts: readonly Fact[];
  readonly permanentMemories: readonly Impression[];
  readonly relevantMemories: readonly Impression[];
  readonly recentInteractions: readonly Event[];
  readonly recentlyHeard: readonly Impression[];
  /** Pending appointments he is party to, as where each stands against now. */
  readonly appointments: readonly BriefedAppointment[];
  readonly secrets: {
    /** What he knows and is hiding: his own, not yet known to the player. */
    readonly hiding: readonly Secret[];
    /** Secrets he knows that the player owns — leverage runs both ways. */
    readonly holdsOnPlayer: readonly Secret[];
  };
  readonly texture: readonly NarrativeTexture[];
  /** Things he told you that the record, or someone else, says otherwise about (§12). */
  readonly contradictions: readonly Contradiction[];
  readonly mode: ConversationMode;
  readonly modeReasons: ModeReasons;
}

function topicTags(topic: Topic): Set<string> {
  const tags = new Set<string>(topic.tags ?? []);
  for (const ref of topic.about ?? []) tags.add(entityTag(ref));
  return tags;
}

/** Does an impression touch the topic: any shared tag. */
export function matchesTopic(impression: Impression, topic: Topic): boolean {
  const wanted = topicTags(topic);
  if (wanted.size === 0) return false;
  return impression.tags.some((t) => wanted.has(t));
}

function briefAppointment(now: WorldTime, a: Appointment): BriefedAppointment {
  return {
    id: a.id,
    place: a.place,
    participants: a.participants,
    purpose: a.purpose,
    promisedBy: a.promisedBy,
    timing: appointmentTiming(now, a),
    minutesFromNow: minutesBetween(now, a.time),
  };
}

/** The player, then everyone at his door. Him alone if he is nowhere reachable. */
function participantsWith(state: WorldState, npcId: NpcId, now: WorldTime): Actor[] {
  const npc = state.npcs[npcId];
  const where = npc ? whereIs(npc, now) : null;
  const present = where === null ? [npcId] : npcsAt(state, where, now);
  return ['player', ...(present.includes(npcId) ? present : [...present, npcId].sort())];
}

export function assembleBriefing(state: WorldState, npcId: NpcId, topic: Topic = {}): Briefing | null {
  const npc = state.npcs[npcId];
  if (!npc) return null;
  const now = state.time;
  const held = impressionsOf(state, npcId, now);

  const permanentMemories = held.filter((i) => i.permanence.tier === 'permanent');
  const relevantMemories = held
    .filter((i) => i.permanence.tier === 'decaying' && matchesTopic(i, topic))
    .slice(0, TUNING.retrieval.decayingMemories);

  const heardWithin = TUNING.retrieval.heardWithinDays * MINUTES_PER_DAY;
  const recentlyHeard = held
    .filter((i) => i.source.kind === 'told' && minutesBetween(i.formedOn, now) <= heardWithin)
    .slice(0, TUNING.retrieval.recentlyHeard);

  // Only what names both of them. Facts about his people are not his to
  // know by virtue of the bond: if he saw it or was told, it is in his
  // impressions, and that is where the model hears it from (§11, §12).
  const facts = factsBetween(state, 'player', npcId).slice(0, TUNING.retrieval.facts);

  const appointments = Object.values(state.appointments)
    .filter((a) => a.status.kind === 'pending' && a.participants.includes(npcId))
    .map((a) => briefAppointment(now, a));

  const secrets = Object.values(state.secrets);
  const hiding = secrets.filter((s) => s.owner === npcId && !s.knownBy.includes('player'));
  const holdsOnPlayer = secrets.filter((s) => s.owner === 'player' && s.knownBy.includes(npcId));

  return {
    npc: {
      id: npc.id,
      name: npc.name,
      role: npc.role,
      voice: npc.voice,
      stats: npc.stats,
      flaw: npc.flaw,
      wants: npc.wants,
      mood: npc.truth.mood,
      injuries: npc.injuries,
    },
    truth: {
      relationship: npc.truth.relationship,
      stances: npc.truth.stances,
      loyalties: npc.truth.loyalties,
    },
    read: {
      relationship: npc.read.relationship,
      stances: npc.read.stances,
      confidence: npc.read.confidence,
      acquaintance: npc.read.acquaintance,
    },
    participants: participantsWith(state, npcId, now),
    addressee: npcId,
    possessions: {
      // OPEN: a save written before `possessions` existed loads with the
      // field missing (ui/persist.ts gates on SAVE_FORMAT only, and that is
      // not this layer's to bump). Reading an absent list as empty is true
      // for such a save — nothing was ever carried — and keeps the briefing
      // from throwing on load. The proper fix is a save-format bump or a
      // migration in /ui.
      npc: npc.truth.possessions ?? [],
      player: state.player.possessions ?? [],
    },
    facts,
    permanentMemories,
    relevantMemories,
    recentInteractions: eventsBetween(state, 'player', npcId).slice(0, TUNING.retrieval.recentInteractions),
    recentlyHeard,
    appointments,
    secrets: { hiding, holdsOnPlayer },
    texture: textureConcerning(state, { kind: 'npc', id: npcId }).slice(0, TUNING.retrieval.texture),
    contradictions: contradictions(state, npcId, 'player'),
    mode: conversationMode(state, npcId),
    modeReasons: modeReasons(state, npcId),
  };
}
